import React from 'react';
import { motion } from 'framer-motion';


import { PageTransition } from '../components/PageTransition';
import { TechStackCard } from '../components/TechStackCard';

export const About: React.FC = () => {
  const architectures = [
    {
      name: "Graph Convolutional Networks (GCN)",
      desc: "Aggregates features from a node's local neighborhood. Effective for general purpose node classification.",
      icon: "🧬",
      color: "from-accent-blue to-accent-cyan"
    },
    {
      name: "Graph Attention Networks (GAT)",
      desc: "Uses attention mechanisms to weigh the importance of neighbor nodes dynamically during aggregation.",
      icon: "🎯",
      color: "from-accent-violet to-fuchsia-500"
    },
    {
      name: "GraphSAGE",
      desc: "Learns a function to sample and aggregate features, enabling inductive learning for unseen nodes.",
      icon: "🧠",
      color: "from-accent-emerald to-teal-400"
    }
  ];

  const techStack = [
    {
      name: "PyTorch Geometric",
      category: "Deep Learning",
      description: "Core framework for GNN implementations, providing optimized sparse tensor operations.",
      icon: <span className="icon-fallback">🖧</span>,
      color: "#f97316"
    },
    {
      name: "FastAPI",
      category: "Backend REST API",
      description: "High-performance Python web framework serving model predictions asynchronously.",
      icon: <span className="icon-fallback">⌨️</span>,
      color: "#10b981"
    },
    {
      name: "React + TypeScript",
      category: "Frontend App",
      description: "Component-based UI with strong static typing for reliable data visualization interfaces.",
      icon: <span className="icon-fallback">💻</span>,
      color: "#6bf7fb"
    },
    {
      name: "Framer Motion",
      category: "UI Animation",
      description: "Declarative animation library powering the smooth transitions and micro-interactions.",
      icon: <span className="icon-fallback">✨</span>,
      color: "#8b5cf6"
    },
    {
      name: "NetworkX / PyG Datasets",
      category: "Graph Processing",
      description: "Python library used for initial graph dataset loading, processing, and metric computation.",
      icon: <span className="icon-fallback">🗄️</span>,
      color: "#94a3b8"
    }
  ];

  return (
    <PageTransition className="about-page space-y-12">
      {/* Header Section */}
      <section className="text-center max-w-3xl mx-auto pt-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(0,251,251,0.1)] border border-[rgba(0,251,251,0.2)] text-accent-blue text-sm font-semibold mb-6 shadow-[0_0_15px_rgba(0,251,251,0.2)]"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-blue opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-blue"></span>
          </span>
          Version 2.0 Pro
        </motion.div>
        
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-extrabold mb-6"
          style={{ color: '#ffffff' }}
        >
          About the Project
        </motion.h1>
        
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-lg text-text-secondary leading-relaxed"
        >
          This dashboard visualizes the capabilities of state-of-the-art Graph Neural Networks (GNNs) applied to the Cora citation dataset. It serves as an interactive portfolio piece demonstrating end-to-end Machine Learning web development.
        </motion.p>
      </section>

      {/* Dataset Section */}
      <section className="glass-card relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent-violet rounded-full blur-[120px] opacity-5 pointer-events-none translate-x-1/3 -translate-y-1/3" />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <span className="text-2xl">📚</span> The Cora Dataset
            </h2>
            <p className="text-text-secondary leading-relaxed mb-6">
              The Cora dataset consists of 2708 machine learning publications classified into one of seven classes. The citation network consists of 5429 links. Each publication in the dataset is described by a 0/1-valued word vector indicating the absence/presence of the corresponding word from the dictionary.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-sm text-text-muted">7 Classes</span>
              <span className="px-3 py-1 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-sm text-text-muted">1433 Features/Node</span>
              <span className="px-3 py-1 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-sm text-text-muted">Transductive Learning</span>
            </div>
          </div>
          <div className="bg-[rgba(15,23,42,0.5)] border border-[rgba(255,255,255,0.05)] rounded-xl p-6 relative group overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-accent-blue to-accent-violet opacity-0 group-hover:opacity-10 transition-opacity duration-500" />
            <pre className="text-xs font-mono text-emerald-400 overflow-x-auto">
              <code>{`{
  "dataset": "Cora",
  "task": "Node Classification",
  "num_nodes": 2708,
  "num_edges": 5429,
  "num_features": 1433,
  "classes": [
    "Case_Based",
    "Genetic_Algorithms",
    "Neural_Networks",
    "Probabilistic_Methods",
    "Reinforcement_Learning",
    "Rule_Learning",
    "Theory"
  ]
}`}</code>
            </pre>
          </div>
        </div>
      </section>

      {/* Architectures Section */}
      <section>
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <span className="text-2xl">🏗️</span> Model Architectures
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {architectures.map((arch, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + (idx * 0.1) }}
              className="bg-bg-glass border border-border-subtle rounded-xl p-6 relative overflow-hidden group hover:border-accent-blue/50 transition-colors"
            >
              <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${arch.color}`} />
              <div className="text-4xl mb-4 group-hover:scale-110 transition-transform origin-bottom-left">{arch.icon}</div>
              <h3 className="text-lg font-bold text-text-primary mb-2">{arch.name}</h3>
              <p className="text-sm text-text-secondary leading-relaxed">{arch.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Tech Stack Section */}
      <section>
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <span className="text-2xl">⚡</span> Technology Stack
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {techStack.map((tech, idx) => (
            <TechStackCard 
              key={idx}
              name={tech.name}
              description={tech.description}
              icon={tech.icon}
              color={tech.color}
              delay={0.3 + (idx * 0.1)}
            />
          ))}
        </div>
      </section>

      {/* Footer Note */}
      <motion.p 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="text-center text-text-muted text-sm pt-8 border-t border-[rgba(255,255,255,0.05)]"
      >
        Designed and developed for portfolio demonstration. © {new Date().getFullYear()}
      </motion.p>
    </PageTransition>
  );
};
